<script lang="ts">
  import {
    createFirmMemo,
    createLawyerMemo,
    deleteCustomerMemo,
    listFirmMemos,
    listLawyerMemos,
    patchCustomerMemo,
  } from '@shared/api/customer-memos';
  import type { CustomerMemo } from '@shared/types/customer-memo';

  const PAGE_SIZE = 200;
  const MAX_MEMO_BODY_CHARS = 4000;

  type MemoTarget =
    | { kind: 'firm'; id: string }
    | { kind: 'lawyer'; id: string };

  interface Props {
    target: MemoTarget;
    canEdit: boolean;
    /// メモ件数 (API の total) の変化を親へ通知する。法律事務所詳細の上位タブ
    /// 件数バッジ用 (spec 20260613-tab-counts-and-review-badges PR1)。
    onCountChange?: (count: number) => void;
  }

  let { target, canEdit, onCountChange }: Props = $props();

  let memos = $state<CustomerMemo[]>([]);
  let total = $state(0);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let draftOpen = $state(false);
  let draftBody = $state('');
  let draftError = $state<string | null>(null);
  let submitting = $state(false);
  let editingId = $state<string | null>(null);
  let editBody = $state('');
  let editError = $state<string | null>(null);
  let savingEdit = $state(false);
  let deletingId = $state<string | null>(null);
  let loadSeq = 0;

  $effect(() => {
    void loadMemos();
  });

  // 件数 (total) を親へ通知する。読み込み中・エラー時は通知しない (親は直前の
  // 値を保持し、一時的な失敗でバッジが消えないようにする)。loadMemos の
  // $effect が先に宣言されているため、マウント直後の total=0 は loading=true で
  // スキップされる。
  $effect(() => {
    if (!loading && !error) onCountChange?.(total);
  });

  async function loadMemos() {
    const seq = ++loadSeq;
    loading = true;
    error = null;
    try {
      const page =
        target.kind === 'firm'
          ? await listFirmMemos(target.id, { page: 1, page_size: PAGE_SIZE })
          : await listLawyerMemos(target.id, { page: 1, page_size: PAGE_SIZE });
      if (seq !== loadSeq) return;
      memos = page.items;
      total = page.total;
    } catch (err) {
      if (seq !== loadSeq) return;
      error = err instanceof Error ? err.message : String(err);
      memos = [];
      total = 0;
    } finally {
      if (seq === loadSeq) loading = false;
    }
  }

  function validateBody(body: string): string | null {
    const length = body.trim().length;
    if (length === 0) return 'メモ本文を入力してください。';
    if (length > MAX_MEMO_BODY_CHARS) return 'メモ本文は4000文字以内で入力してください。';
    return null;
  }

  function openDraft() {
    draftOpen = true;
    draftBody = '';
    draftError = null;
    cancelEdit();
  }

  function closeDraft() {
    draftOpen = false;
    draftBody = '';
    draftError = null;
  }

  async function handleCreate(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting || !canEdit) return;
    const validation = validateBody(draftBody);
    if (validation) {
      draftError = validation;
      return;
    }

    submitting = true;
    draftError = null;
    try {
      const body = { body: draftBody.trim() };
      const memo =
        target.kind === 'firm'
          ? await createFirmMemo(target.id, body)
          : await createLawyerMemo(target.id, body);
      memos = [memo, ...memos];
      total += 1;
      closeDraft();
    } catch (err) {
      draftError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  function startEdit(memo: CustomerMemo) {
    if (!canEdit) return;
    editingId = memo.id;
    editBody = memo.body;
    editError = null;
    closeDraft();
  }

  function cancelEdit() {
    editingId = null;
    editBody = '';
    editError = null;
  }

  async function saveEdit(memo: CustomerMemo) {
    if (savingEdit || !canEdit) return;
    const validation = validateBody(editBody);
    if (validation) {
      editError = validation;
      return;
    }

    savingEdit = true;
    editError = null;
    try {
      const updated = await patchCustomerMemo(memo.id, { body: editBody.trim() });
      memos = memos.map((item) => (item.id === updated.id ? updated : item));
      cancelEdit();
    } catch (err) {
      editError = err instanceof Error ? err.message : String(err);
    } finally {
      savingEdit = false;
    }
  }

  async function handleDelete(memo: CustomerMemo) {
    if (deletingId || !canEdit) return;
    const confirmed = confirm('このメモを削除します。よろしいですか?');
    if (!confirmed) return;

    deletingId = memo.id;
    try {
      await deleteCustomerMemo(memo.id);
      memos = memos.filter((item) => item.id !== memo.id);
      total = Math.max(0, total - 1);
      if (editingId === memo.id) cancelEdit();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      deletingId = null;
    }
  }

  function sourceLabel(memo: CustomerMemo): string {
    if (target.kind === 'lawyer') return '弁護士メモ';
    if (memo.lawyer_id) return `弁護士: ${memo.lawyer_name ?? memo.lawyer_id}`;
    return '事務所メモ';
  }

  function formatDateTime(iso: string): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso.length >= 16 && iso.includes('T') ? iso.slice(0, 16).replace('T', ' ') : iso;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }
</script>

<section class="memo-panel" aria-label="メモ">
  <header class="memo-header">
    <h3>メモ</h3>
    <button
      type="button"
      class="btn btn-primary"
      disabled={!canEdit}
      onclick={openDraft}
      aria-label="メモを追加"
    >
      + メモを追加
    </button>
  </header>

  {#if draftOpen}
    <form class="memo-form" onsubmit={handleCreate}>
      <label for="customer-memo-draft">メモ本文</label>
      <textarea
        id="customer-memo-draft"
        bind:value={draftBody}
        rows="5"
        maxlength={MAX_MEMO_BODY_CHARS}
        placeholder="メモを入力"
      ></textarea>
      <div class="form-footer">
        <span class="count">{draftBody.trim().length} / {MAX_MEMO_BODY_CHARS}</span>
        <div class="form-actions">
          <button type="button" class="btn" onclick={closeDraft} disabled={submitting}>
            キャンセル
          </button>
          <button type="submit" class="btn btn-primary" disabled={submitting}>
            {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
      {#if draftError}<p class="form-error">{draftError}</p>{/if}
    </form>
  {/if}

  {#if loading && memos.length === 0}
    <p class="status">読み込み中…</p>
  {:else if error}
    <p class="status error">{error}</p>
  {/if}

  {#if !loading && !error && memos.length === 0}
    <p class="empty-state">メモはまだありません。</p>
  {:else if memos.length > 0}
    <div class="memo-count">{memos.length} / {total} 件表示中</div>
    <ol class="memo-list">
      {#each memos as memo (memo.id)}
        <li class="memo-card">
          <div class="memo-meta">
            <span class="source">{sourceLabel(memo)}</span>
            <span>{formatDateTime(memo.created_at)}</span>
            <span>{memo.created_by_name ?? '作成者不明'}</span>
          </div>

          {#if editingId === memo.id}
            <div class="edit-block">
              <textarea
                bind:value={editBody}
                rows="5"
                maxlength={MAX_MEMO_BODY_CHARS}
                aria-label="メモ本文を編集"
              ></textarea>
              <div class="form-footer">
                <span class="count">{editBody.trim().length} / {MAX_MEMO_BODY_CHARS}</span>
                <div class="form-actions">
                  <button type="button" class="btn" onclick={cancelEdit} disabled={savingEdit}>
                    キャンセル
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onclick={() => saveEdit(memo)}
                    disabled={savingEdit}
                  >
                    {savingEdit ? '保存中…' : '保存'}
                  </button>
                </div>
              </div>
              {#if editError}<p class="form-error">{editError}</p>{/if}
            </div>
          {:else}
            <p class="memo-body">{memo.body}</p>
            {#if canEdit}
              <div class="memo-actions">
                <button type="button" class="text-button" onclick={() => startEdit(memo)}>
                  編集
                </button>
                <button
                  type="button"
                  class="text-button danger"
                  disabled={deletingId === memo.id}
                  onclick={() => handleDelete(memo)}
                >
                  {deletingId === memo.id ? '削除中…' : '削除'}
                </button>
              </div>
            {/if}
          {/if}
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  .memo-panel {
    display: grid;
    gap: 12px;
  }

  .memo-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .memo-header h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }

  .memo-form,
  .edit-block {
    display: grid;
    gap: 8px;
  }

  .memo-form {
    padding: 12px;
    border: 1px solid #dbe3ef;
    border-radius: 6px;
    background: #fff;
  }

  .memo-form label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #374151;
  }

  textarea {
    width: 100%;
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .form-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .form-actions,
  .memo-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .count,
  .memo-count {
    color: #6b7280;
    font-size: 0.75rem;
  }

  .form-error,
  .status.error {
    margin: 0;
    color: #b91c1c;
    font-size: 0.8125rem;
  }

  .status,
  .empty-state {
    margin: 0;
    padding: 16px;
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    color: #6b7280;
    background: #fff;
  }

  .memo-list {
    display: grid;
    gap: 10px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .memo-card {
    display: grid;
    gap: 8px;
    padding: 12px;
    border: 1px solid #dbe3ef;
    border-radius: 6px;
    background: #fff;
  }

  .memo-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 0.75rem;
  }

  .source {
    padding: 2px 8px;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-weight: 600;
  }

  .memo-body {
    margin: 0;
    color: #111827;
    font-size: 0.875rem;
    line-height: 1.65;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .memo-actions {
    justify-content: flex-end;
  }

  .text-button {
    border: 0;
    background: transparent;
    color: #2563eb;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
    padding: 2px 4px;
  }

  .text-button.danger {
    color: #b91c1c;
  }

  @media (max-width: 640px) {
    .memo-header,
    .form-footer {
      align-items: stretch;
      flex-direction: column;
    }

    .form-actions,
    .memo-actions {
      justify-content: flex-end;
    }
  }
</style>
